/**
 * @file The entry step's action row: how many parts are staged, Clear all, and Start.
 * Consumer: `SignifyFetchJobEntry`. It subscribes to the row count alone, so it re-renders on an
 * add, a remove or a clear and never on an edit to a row.
 * Start carries its reason for being disabled in its own label, and Clear all asks for confirmation
 * past the same threshold the ArTable bulk actions use. Neither control is ever hidden.
 * "Also save as record" is not here: the record is written by the insert that follows the fetch, so
 * the switch lives in the progress step's footer where that decision is made and can still change.
 */

import { FC } from 'react';
import { Button, Popconfirm, Typography } from 'antd';
import { useSnapshot } from 'valtio';
import { BULK_CONFIRM_THRESHOLD } from '#types';
import FlexBox from '@/components/atoms/FlexBox';
import type { SignifyFetchJobDraft } from '../index.types';

const { Text } = Typography;

type SignifyFetchJobEntryActionsProps = {
  draft: SignifyFetchJobDraft;
  configDisabledReason: string | null;
  distributorSelected: boolean;
  starting: boolean;
  onClear: () => void;
  onStart: () => void;
};

const SignifyFetchJobEntryActions: FC<SignifyFetchJobEntryActionsProps> = ({
  draft,
  configDisabledReason,
  distributorSelected,
  starting,
  onClear,
  onStart,
}) => {
  const { keys } = useSnapshot(draft);
  const count = keys.length;

  const startDisabledReason = configDisabledReason
    ?? (count === 0 ? 'Add parts to fetch' : null)
    ?? (!distributorSelected ? 'Choose a distributor' : null);

  return (
    <FlexBox justifyStart gap={10} wrap>
      <Text>Parts to fetch ({count})</Text>

      <Popconfirm
        title={`Clear all ${count} ${count === 1 ? 'part' : 'parts'}?`}
        okText="Clear all"
        cancelText="Cancel"
        disabled={count <= BULK_CONFIRM_THRESHOLD}
        onConfirm={onClear}
      >
        <Button
          aria-label="signify-fetch-job-clear-all"
          disabled={count === 0}
          onClick={() => {
            if (count <= BULK_CONFIRM_THRESHOLD) onClear();
          }}
        >
          {count === 0 ? 'Nothing to clear' : `Clear all (${count})`}
        </Button>
      </Popconfirm>

      <Button
        type="primary"
        aria-label={
          startDisabledReason
            ? `signify-fetch-job-start (${startDisabledReason})`
            : 'signify-fetch-job-start'
        }
        disabled={!!startDisabledReason}
        loading={starting}
        onClick={onStart}
      >
        {startDisabledReason ?? `Fetch ${count} ${count === 1 ? 'part' : 'parts'}`}
      </Button>
    </FlexBox>
  );
};

export default SignifyFetchJobEntryActions;
